import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getStructureOccupancy } from "../api/rest";
import { flattenOccupancyTree } from "./structureOccupancy";
import { CollapsibleSection } from "../app/layout/CollapsibleSection";
import run from "./InventoryRunPage.module.css";

/** Depth shown without asking (ADR 233): the zone and its aisles. Everything
 *  below — racks, levels — is one click away, per branch. */
const OPEN_TO_DEPTH = 1;

/** Percent on a 0–100 scale, 1 dp, decimal comma (pt-BR) — mirrors the run report's fmtPct1. */
function fmtPct1(x: number): string {
  return `${x.toFixed(1).replace(".", ",")}%`;
}

/**
 * ADR 171, slice S6: one run's occupancy grouped by the canonical structure graph — an
 * indented Zone → Aisle → Rack → Level tree, each node a stacked occupied / empty /
 * not-scanned bar over the occupiable bound Spaces beneath it. Run-scoped (the run's own
 * warehouseId + runId); the structure-aware counterpart to the address-parsed aisle coverage
 * on the same report. Self-hides when the warehouse has no derived structure or nothing
 * occupiable in it.
 */
export function StructureOccupancySection({ siteId, warehouseId, runId }: {
  siteId: string;
  warehouseId: string;
  runId: string;
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["structure-occupancy", siteId, warehouseId, runId],
    queryFn: () => getStructureOccupancy(siteId, warehouseId, runId),
    retry: 0,
  });

  // Branches whose state the operator FLIPPED away from the default — so the same
  // control closes a shallow branch and opens a deep one.
  const [flipped, setFlipped] = useState<ReadonlySet<string>>(() => new Set());
  const [expandAll, setExpandAll] = useState(false);
  const nodes = query.data?.nodes ?? [];
  const isOpen = (id: string, depth: number) =>
    expandAll || (depth < OPEN_TO_DEPTH ? !flipped.has(id) : flipped.has(id));
  const rows = useMemo(
    () => flattenOccupancyTree(nodes, (node, depth) => isOpen(node.id, depth)),
    // isOpen is derived from exactly these three.
    [nodes, flipped, expandAll],
  );
  if (rows.length === 0) return null;

  function toggle(id: string) {
    setFlipped((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  return (
    <CollapsibleSection
      id="run.structureOccupancy"
      title={t("inventory.structureOccupancy.title")}
      data-testid="structure-occupancy-section"
    >
      <p className={run.note}>{t("inventory.structureOccupancy.intro")}</p>
      <div className={run.structActions}>
        <button type="button" className={run.linkBtn} onClick={() => setExpandAll((v) => !v)}>
          {t(expandAll ? "inventory.structureOccupancy.collapse" : "inventory.structureOccupancy.expand")}
        </button>
        <span className={run.note}>
          {t("inventory.structureOccupancy.shown", { shown: rows.length })}
        </span>
      </div>
      <div className={run.structTree} role="list">
        {rows.map(({ node, depth, hasChildren }) => {
          const o = node.occupancy;
          const total = o.occupiableSpaces || 1; // flattened rows always have occupiableSpaces > 0
          const kindLabel = t(`inventory.structureOccupancy.kind.${node.kind}`);
          const name = node.code ?? node.name ?? node.path.split("/").filter(Boolean).pop() ?? node.id;
          const pctLabel = o.occupancyPct == null ? "—" : fmtPct1(o.occupancyPct);
          return (
            <div
              className={run.structRow}
              key={node.id}
              role="listitem"
              style={{ paddingInlineStart: `${depth * 18}px` }}
              aria-label={t("inventory.structureOccupancy.rowAria", {
                kind: kindLabel,
                name,
                occupied: o.occupiedSpaces,
                occupiable: o.occupiableSpaces,
                pct: pctLabel,
              })}
            >
              <div className={run.structLabel}>
                {hasChildren && !expandAll ? (
                  <button
                    type="button"
                    className={run.structToggle}
                    aria-expanded={isOpen(node.id, depth)}
                    aria-label={`${kindLabel} ${name}`}
                    onClick={() => toggle(node.id)}
                  >
                    {isOpen(node.id, depth) ? "▾" : "▸"}
                  </button>
                ) : (
                  <span className={run.structToggle} aria-hidden />
                )}
                <span className={run.structKind}>{kindLabel}</span>
                <span className={run.structName}>{name}</span>
              </div>
              <div className={run.structBar} aria-hidden>
                <div className={run.structOccupied} style={{ width: `${(100 * o.occupiedSpaces) / total}%` }} />
                <div className={run.structEmpty} style={{ width: `${(100 * o.emptySpaces) / total}%` }} />
                <div className={run.structNotScanned} style={{ width: `${(100 * o.notScannedSpaces) / total}%` }} />
              </div>
              <div className={run.structPct}>{pctLabel}</div>
              <div className={run.structCounts}>
                {t("inventory.structureOccupancy.counts", {
                  occupied: o.occupiedSpaces,
                  occupiable: o.occupiableSpaces,
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className={run.legend}>{t("inventory.structureOccupancy.legend")}</p>
    </CollapsibleSection>
  );
}
